/**
 * CTRL_02 Dashboard - Model Risk Classification
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, Table, Tag, Spin, Empty } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

const RISK_LEVELS = {
  'unacceptable': { label: 'Unacceptable Risk', color: '#f5222d', icon: CloseCircleOutlined },
  'high': { label: 'High Risk', color: '#fa8c16', icon: ExclamationCircleOutlined },
  'limited': { label: 'Limited Risk', color: '#faad14', icon: WarningOutlined },
  'minimal': { label: 'Minimal Risk', color: '#52c41a', icon: CheckCircleOutlined }
};

function CTRL_02_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_02';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate risk distribution
  const calculateRiskDistribution = () => {
    const distribution = {
      unacceptable: 0,
      high: 0,
      limited: 0,
      minimal: 0
    };

    events.forEach(event => {
      const riskLevel = event.event_metadata?.risk_level || event.event_metadata?.risk_classification;
      if (riskLevel && distribution.hasOwnProperty(riskLevel.toLowerCase())) {
        distribution[riskLevel.toLowerCase()]++;
      }
    });

    return distribution;
  };

  // Calculate classification coverage
  const calculateCoverage = () => {
    if (events.length === 0) return { total: 0, classified: 0, percentage: 0 };

    const classified = events.filter(e => {
      const metadata = e.event_metadata || {};
      return metadata.risk_level || metadata.risk_classification;
    }).length;

    return {
      total: events.length,
      classified,
      percentage: Math.round((classified / events.length) * 100)
    };
  };

  // Calculate high-risk oversight
  const calculateHighRiskOversight = () => {
    const highRiskEvents = events.filter(e => {
      const riskLevel = (e.event_metadata?.risk_level || e.event_metadata?.risk_classification || '').toLowerCase();
      return riskLevel === 'high' || riskLevel === 'unacceptable';
    });

    const withOversight = highRiskEvents.filter(e => e.event_metadata?.human_oversight === true).length;

    return {
      total: highRiskEvents.length,
      withOversight,
      withoutOversight: highRiskEvents.length - withOversight,
      percentage: highRiskEvents.length > 0 ? Math.round((withOversight / highRiskEvents.length) * 100) : 0
    };
  };

  const distribution = calculateRiskDistribution();
  const coverage = calculateCoverage();
  const oversight = calculateHighRiskOversight();

  // Table columns for recent classifications
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (ts) => new Date(ts).toLocaleString(),
      width: 180
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      ellipsis: true
    },
    {
      title: 'Risk Level',
      key: 'risk_level',
      render: (_, record) => {
        const riskLevel = (record.event_metadata?.risk_level || record.event_metadata?.risk_classification || 'unknown').toLowerCase();
        const riskInfo = RISK_LEVELS[riskLevel] || { label: 'Unknown', color: '#888', icon: WarningOutlined };
        const IconComponent = riskInfo.icon;

        return (
          <Tag color={riskInfo.color} icon={<IconComponent />}>
            {riskInfo.label}
          </Tag>
        );
      }
    },
    {
      title: 'Use Case',
      dataIndex: ['event_metadata', 'use_case'],
      key: 'use_case',
      ellipsis: true,
      render: (useCase) => useCase || '-'
    },
    {
      title: 'Human Oversight',
      key: 'oversight',
      align: 'center',
      render: (_, record) => {
        const hasOversight = record.event_metadata?.human_oversight === true;
        return hasOversight ?
          <Tag color="green" icon={<CheckCircleOutlined />}>Yes</Tag> :
          <Tag color="red" icon={<CloseCircleOutlined />}>No</Tag>;
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <Empty
        description="Please select a project to view dashboard"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_02: Model Risk Classification Dashboard</h2>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Models"
              value={events.length}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Classification Coverage"
              value={coverage.percentage}
              suffix="%"
              prefix={coverage.percentage >= 70 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: coverage.percentage >= 70 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {coverage.classified} of {coverage.total} models classified
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="High-Risk Oversight"
              value={oversight.percentage}
              suffix="%"
              prefix={oversight.percentage >= 80 ? <CheckCircleOutlined /> : <WarningOutlined />}
              valueStyle={{ color: oversight.percentage >= 80 ? '#52c41a' : '#faad14' }}
            />
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
              {oversight.withOversight} of {oversight.total} high-risk models
            </div>
          </Card>
        </Col>
      </Row>

      {/* Risk Distribution */}
      <Card title="Risk Distribution" style={{ marginBottom: '24px' }}>
        <Row gutter={16}>
          {Object.entries(RISK_LEVELS).map(([key, info]) => {
            const IconComponent = info.icon;
            const count = distribution[key];
            const percentage = events.length > 0 ? Math.round((count / events.length) * 100) : 0;

            return (
              <Col xs={24} sm={12} md={6} key={key}>
                <Card
                  size="small"
                  style={{
                    backgroundColor: `${info.color}15`,
                    borderColor: info.color
                  }}
                >
                  <Statistic
                    title={info.label}
                    value={count}
                    prefix={<IconComponent style={{ color: info.color }} />}
                    valueStyle={{ color: info.color }}
                  />
                  <Progress
                    percent={percentage}
                    strokeColor={info.color}
                    size="small"
                    style={{ marginTop: '8px' }}
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* High-Risk Oversight Alert */}
      {oversight.withoutOversight > 0 && (
        <Card
          style={{
            marginBottom: '24px',
            backgroundColor: '#fff2e8',
            borderColor: '#fa8c16'
          }}
        >
          <Row align="middle" gutter={16}>
            <Col>
              <WarningOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />
            </Col>
            <Col flex={1}>
              <div style={{ fontWeight: 'bold', color: '#d46b08' }}>
                {oversight.withoutOversight} High-Risk Model(s) Without Human Oversight
              </div>
              <div style={{ color: '#ad6800', fontSize: '13px' }}>
                High-risk and unacceptable-risk models require human oversight to ensure safe deployment
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* Recent Classifications Table */}
      <Card title="Recent Risk Classifications">
        <Table
          dataSource={events.slice(0, 20)}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} classifications`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_02_Dashboard;
